import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Container, Header, Icon, Input, Message } from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useToast } from './utils/Toast';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { isMockAuthEnabled, clearMockSession, getMockSites } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import {
  fetchAccessibleSites,
  SiteRecord,
  AuthTokenExpiredError,
  AccessDeniedError
} from './api/sites';
import './SiteDashboard.css';

type ViewState = {
  loading: boolean;
  error: string | null;
};

const ClientDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, refreshSession, loading: authLoading } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [viewState, setViewState] = useState<ViewState>({ loading: true, error: null });

  const logoSrc = theme === 'dark'
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const loadSites = useCallback(async () => {
    if (authLoading) {
      return;
    }

    setViewState({ loading: true, error: null });

    try {
      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockSites = getMockSites() as any;
        setSites(mockSites);
        setViewState({ loading: false, error: null });
        return;
      }
      
      const response = await fetchAccessibleSites();
      setSites(response);
      setViewState({ loading: false, error: null });
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }

      if (error instanceof AccessDeniedError) {
        setViewState({ loading: false, error: null });
        showToast('You do not have permission to view these sites.', 'error');
        navigate('/no-access', { replace: true, state: { from: '/user-dashboard' } });
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to load sites at the moment.';
      setViewState({ loading: false, error: message });
      showToast(message, 'error');
    }
  }, [authLoading, navigate, showToast]);

  useEffect(() => {
    if (!authLoading) {
      void loadSites();
    }
  }, [authLoading, loadSites]);

  const handleLogout = async () => {
    try {
      if (isMockAuthEnabled()) {
        clearMockSession();
        await refreshSession();
        showToast('Logged out successfully', 'success');
        navigate('/');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      await refreshSession();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch (err) {
      console.error('[ClientDashboard] Failed to sign out', err);
      showToast('Unable to log out right now. Please try again.', 'error');
    }
  };

  const handleSiteClick = (siteId: string) => {
    navigate(`/sites/${siteId}`);
  };

  const filteredSites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sites;
    }

    return sites.filter((site) => {
      const nameMatch = site.siteName?.toLowerCase().includes(query);
      const locationMatch = site.location?.toLowerCase().includes(query);
      return Boolean(nameMatch || locationMatch);
    });
  }, [sites, searchQuery]);

  const hasAnySites = sites.length > 0;
  const emptyStateMessage = hasAnySites
    ? 'No sites match your search.'
    : 'No sites have been shared with this account yet.';
  const emptyStateIcon = hasAnySites ? 'search' : 'archive';

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Container>
          <div className="header-content">
            <div className="header-logo">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="dashboard-logo" />
            </div>
            <div className="header-actions">
              <div className="header-user" title={user?.email ?? undefined}>
                <Icon name="user circle" />
                <span className="user-name">{user?.user_metadata?.full_name?.trim() || user?.email || 'Member'}</span>
              </div>
              <Button
                className="logout-button"
                onClick={handleLogout}
                icon
                labelPosition="right"
              >
                <Icon name="sign out" />
                Logout
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container className="dashboard-main">
        <div className="dashboard-title-section">
          <Header as="h1" className="dashboard-title">
            User Dashboard
          </Header>
          <p className="dashboard-subtitle">Browse the sites that have been shared with your account.</p>
        </div>

        <div className="dashboard-controls">
          <div className="search-container">
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search your sites..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="search-input"
              size="large"
            />
          </div>
        </div>

        <div className="sites-grid">
          {viewState.loading ? (
            <div className="loading-message">Loading your sites...</div>
          ) : viewState.error ? (
            <Message negative>
              <Message.Header>Unable to load sites</Message.Header>
              <p>{viewState.error}</p>
              <Button basic color="red" onClick={() => { void loadSites(); }} className="retry-button">
                <Icon name="refresh" /> Try again
              </Button>
            </Message>
          ) : filteredSites.length === 0 ? (
            <div className="empty-message">
              <Icon name={emptyStateIcon} size="huge" className="empty-icon" />
              <p>{emptyStateMessage}</p>
            </div>
          ) : (
            filteredSites.map((site) => {
              const hazardCount = site.hazardEntries?.length ?? 0;
              return (
                <Card
                  key={site.id}
                  className="site-card"
                  onClick={() => handleSiteClick(site.id)}
                >
                  <Card.Content>
                    <Card.Header className="site-card-header">
                      {site.siteName || 'Untitled site'}
                    </Card.Header>
                    <Card.Meta className="site-card-meta">
                      <Icon name="map marker alternate" aria-hidden="true" />
                      <span>{site.location || 'Location not specified'}</span>
                    </Card.Meta>
                    <Card.Description className="site-card-description">
                      <div className="site-info-row">
                        <span className="info-label">Shared on</span>
                        <span className="info-value">
                          {site.createdAt ? new Date(site.createdAt).toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                      <div className="site-info-row">
                        <span className="info-label">Hazards</span>
                        <span className="info-value">{hazardCount}</span>
                      </div>
                    </Card.Description>
                  </Card.Content>
                </Card>
              );
            })
          )}
        </div>

        {filteredSites.length > 0 && !viewState.loading && !viewState.error && (
          <div className="dashboard-footer">
            <p>Showing {filteredSites.length} of {sites.length} sites</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ClientDashboard;
